import { supabase } from '../config/supabase'

/**
 * Obtiene el wedding_id del usuario (considerando si es partner)
 */
const getUserWeddingId = async (userId) => {
  try {
    const { data: profile } = await supabase
      .from('profiles')
      .select('partner_user_id')
      .eq('id', userId)
      .maybeSingle()

    const weddingOwnerId = profile?.partner_user_id || userId

    const { data, error } = await supabase
      .from('weddings')
      .select('id')
      .eq('user_id', weddingOwnerId)
      .order('created_at', { ascending: false })
      .limit(1)
      .maybeSingle()

    if (error && error.code !== 'PGRST116') {
      console.error('Error fetching wedding ID:', error)
      return null
    }
    return data?.id || null
  } catch (error) {
    console.error('Error getting wedding ID:', error)
    return null
  }
}

/**
 * Obtiene todos los proveedores
 * @param {string} userId
 * @param {boolean} [bookedOnly] - si true, solo los contratados; si false, solo comparativo; si null, todos
 * @returns {Promise<Array>}
 */
export const getSuppliers = async (userId, bookedOnly = null) => {
  try {
    const weddingId = await getUserWeddingId(userId)
    if (!weddingId) return []

    const { data, error } = await supabase
      .from('suppliers')
      .select('*')
      .eq('wedding_id', weddingId)
      .order('created_at', { ascending: false })

    if (error) throw error
    let list = data || []

    if (bookedOnly === true) {
      list = list.filter(s => s.booked === true)
    } else if (bookedOnly === false) {
      list = list.filter(s => !s.booked)
    }
    return list
  } catch (error) {
    console.error('Error fetching suppliers:', error)
    return []
  }
}

/**
 * Crea un nuevo proveedor
 */
export const createSupplier = async (userId, supplierData) => {
  try {
    const weddingId = await getUserWeddingId(userId)
    if (!weddingId) {
      throw new Error('No wedding found for user')
    }

    const insertData = {
      wedding_id: weddingId,
      name: supplierData.name || supplierData.business_name || '',
      category: supplierData.category || supplierData.vendor || '',
      contact_name: supplierData.contact_name || null,
      email: supplierData.email || null,
      phone: supplierData.phone || null,
      rate: supplierData.rate || supplierData.total_cost_package || null,
      notes: supplierData.notes || supplierData.inclusions_notes || null,
      booked: supplierData.booked || false,
    }

    // Campos extendidos (si existen en la tabla)
    const optionalFields = [
      'business_name', 'web', 'arrival_time', 'end_time', 'meal_required',
      'dietaries', 'total_cost_package', 'inclusions_notes', 'budget_item',
      'initial_payment', 'initial_due_date', 'initial_paid',
      'second_payment', 'second_due_date', 'second_paid', 'documents'
    ]
    optionalFields.forEach(field => {
      if (supplierData[field] !== undefined && supplierData[field] !== null) {
        insertData[field] = supplierData[field]
      }
    })

    const { data, error } = await supabase
      .from('suppliers')
      .insert([insertData])
      .select()
      .single()

    if (error) throw error
    return data
  } catch (error) {
    console.error('Error creating supplier:', error)
    throw error
  }
}

/**
 * Actualiza un proveedor
 */
export const updateSupplier = async (supplierId, updates) => {
  try {
    const updateData = {}
    const allowedFields = [
      'name', 'business_name', 'category', 'contact_name', 'email', 'phone',
      'rate', 'notes', 'booked', 'web', 'arrival_time', 'end_time',
      'meal_required', 'dietaries', 'total_cost_package', 'inclusions_notes',
      'budget_item', 'initial_payment', 'initial_due_date', 'initial_paid',
      'second_payment', 'second_due_date', 'second_paid', 'documents'
    ]
    allowedFields.forEach(field => {
      if (updates[field] !== undefined) {
        updateData[field] = updates[field]
      }
    })

    const { data, error } = await supabase
      .from('suppliers')
      .update(updateData)
      .eq('id', supplierId)
      .select()
      .single()

    if (error) throw error
    return data
  } catch (error) {
    console.error('Error updating supplier:', error)
    throw error
  }
}

/**
 * Elimina un proveedor
 */
export const deleteSupplier = async (supplierId) => {
  try {
    const { error } = await supabase
      .from('suppliers')
      .delete()
      .eq('id', supplierId)
    if (error) throw error
  } catch (error) {
    console.error('Error deleting supplier:', error)
    throw error
  }
}
